import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Bot, User, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

interface Message {
  id: string;
  role: 'user' | 'bot';
  content: string;
  timestamp: Date;
}

const MAX_MESSAGES = 10;
const RATE_LIMIT_COOLDOWN = 60; // seconds

export function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'bot',
      content: "Hi! I'm KojiBot. Ask me anything about Koji — pricing, features, setup, or how it works.",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [rateLimitHit, setRateLimitHit] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setRateLimitHit(false);
    }
  }, [cooldown]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || loading || rateLimitHit) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev.slice(-MAX_MESSAGES + 1), userMessage]);
    setInput('');
    setLoading(true);

    const res = await api.send<{ response: string; model: string }>('/api/v1/chat', {
      method: 'POST',
      body: JSON.stringify({ message: userMessage.content }),
    });

    setLoading(false);

    if (res.success && res.data) {
      const botMessage: Message = {
        id: crypto.randomUUID(),
        role: 'bot',
        content: res.data.response,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev.slice(-MAX_MESSAGES + 1), botMessage]);
    } else if (res.error?.includes('Rate limit')) {
      setRateLimitHit(true);
      setCooldown(RATE_LIMIT_COOLDOWN);
      const errorMessage: Message = {
        id: crypto.randomUUID(),
        role: 'bot',
        content: `⏱️ ${res.error}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } else {
      const errorMessage: Message = {
        id: crypto.randomUUID(),
        role: 'bot',
        content: 'Sorry, I had trouble processing that. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  }, [input, loading, rateLimitHit]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-[150]">
      {/* Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="w-14 h-14 rounded-full bg-[#e5ac41] text-[#0a0a0a] shadow-lg hover:scale-110 transition-all duration-200 flex items-center justify-center"
          aria-label="Open chat"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className="w-[360px] h-[500px] bg-[#111111] border border-[#1a1a1a] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#1a1a1a]">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#e5ac41]/10 flex items-center justify-center">
                <Bot className="w-4 h-4 text-[#e5ac41]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#f1f5f9]">KojiBot</p>
                <p className="text-[10px] text-[#34d399]">Online</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-[#64748b] hover:text-[#f1f5f9] transition-colors"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === 'bot' ? 'bg-[#e5ac41]/10' : 'bg-[#1a1a1a]'
                }`}>
                  {msg.role === 'bot' ? (
                    <Bot className="w-3.5 h-3.5 text-[#e5ac41]" />
                  ) : (
                    <User className="w-3.5 h-3.5 text-[#94a3b8]" />
                  )}
                </div>
                <div className={`max-w-[260px] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                  msg.role === 'bot'
                    ? 'bg-[#0a0a0a] border border-[#1a1a1a] text-[#f1f5f9]'
                    : 'bg-[#e5ac41] text-[#0a0a0a]'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-[#e5ac41]/10 flex items-center justify-center">
                  <Bot className="w-3.5 h-3.5 text-[#e5ac41]" />
                </div>
                <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl px-3 py-2">
                  <Loader2 className="w-4 h-4 text-[#e5ac41] animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-[#1a1a1a]">
            {rateLimitHit && (
              <p className="text-xs text-[#e5ac41] mb-2">
                Rate limit hit. Try again in {cooldown}s
              </p>
            )}
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={rateLimitHit ? 'Wait for cooldown...' : 'Ask about Koji...'}
                disabled={loading || rateLimitHit}
                maxLength={1000}
                className="flex-1 px-3 py-2 bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg text-sm text-[#f1f5f9] placeholder:text-[#64748b] focus:outline-none focus:border-[#e5ac41] transition-colors disabled:opacity-50"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading || rateLimitHit}
                className="p-2 rounded-lg bg-[#e5ac41] text-[#0a0a0a] hover:bg-[#d39737] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
